import React, { useMemo } from 'react';
import TokenIcon from './TokenIcon';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';

function getTrendingPools(pools) {
  const items = [];

  pools.forEach(p => {
    if (p.apyPct1D != null && p.apyPct1D > 5) {
      items.push({
        type: 'aprSpike',
        label: 'APR Spike',
        pool: p,
        token: p.tokens?.[0] || 'UNKNOWN',
        name: p.name,
        change: p.apyPct1D,
        positive: true,
        magnitude: p.apyPct1D,
      });
    }

    if (p.apyPct7D != null && p.apyPct7D > 10) {
      items.push({
        type: 'weeklyHot',
        label: 'Weekly Hot',
        pool: p,
        token: p.tokens?.[0] || 'UNKNOWN',
        name: p.name,
        change: p.apyPct7D,
        positive: true,
        magnitude: p.apyPct7D,
      });
    }

    if (p.volume24h && p.tvl && (p.volume24h / p.tvl) > 0.5) {
      const ratio = p.volume24h / p.tvl;
      items.push({
        type: 'highActivity',
        label: 'High Activity',
        pool: p,
        token: p.tokens?.[0] || 'UNKNOWN',
        name: p.name,
        change: Math.round(ratio * 100),
        positive: true,
        magnitude: ratio * 100,
      });
    }
  });

  items.sort((a, b) => b.magnitude - a.magnitude);
  return items.slice(0, 30);
}

export default function TrendingBar({ pools, onSelectPool }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();

  const items = useMemo(() => {
    const poolTrending = getTrendingPools(pools);
    if (poolTrending.length === 0) return [];
    return [...poolTrending, ...poolTrending];
  }, [pools]);

  if (items.length === 0) return null;

  const labelColors = {
    aprSpike: { color: colors.gold, bg: colors.goldBg, border: colors.goldBorder },
    weeklyHot: { color: colors.red, bg: colors.redBg, border: colors.redBorder },
    highActivity: { color: colors.green, bg: colors.greenBg, border: colors.greenBorder },
  };

  return (
    <div style={{
      background: colors.trendingBg,
      borderBottom: `1px solid ${colors.trendingBorder}`,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      padding: isMobile ? '6px 0' : '8px 0',
      position: 'relative',
      transition: 'border-color 0.3s ease',
    }}>
      {/* Left label */}
      <div style={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: isMobile ? 80 : 110,
        background: colors.trendingFadeLeft,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 16,
        gap: 6,
      }}>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: isMobile ? 8 : 10,
          fontWeight: 700,
          color: colors.gold,
          letterSpacing: isMobile ? 1 : 1.5,
          animation: 'trendingPulse 2s ease-in-out infinite',
        }}>
          {'\u26A1'} TRENDING
        </span>
      </div>

      {/* Scrolling items */}
      <div style={{
        display: 'inline-flex',
        animation: `trendingScroll ${Math.max(20, items.length * 2.5)}s linear infinite`,
        gap: isMobile ? 16 : 24,
        paddingLeft: isMobile ? 90 : 120,
      }}>
        {items.map((item, i) => {
          const tagStyle = labelColors[item.type] || labelColors.aprSpike;
          return (
            <div key={`${item.type}-${item.name}-${i}`} onClick={() => {
              if (item.pool && onSelectPool) onSelectPool(item.pool);
            }} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              flexShrink: 0,
              padding: '4px 8px',
              borderRadius: 6,
              cursor: item.pool ? 'pointer' : 'default',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (item.pool) e.currentTarget.style.background = colors.hover; }}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <TokenIcon symbol={item.token} size={18} />
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: isMobile ? 9 : 11,
                color: colors.textTertiary,
                fontWeight: 500,
              }}>
                {item.name}
              </span>
              {item.change != null && !isMobile && (
                <span style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  color: item.positive ? colors.green : colors.red,
                  fontWeight: 600,
                }}>
                  {item.positive ? '\u25B2' : '\u25BC'}
                  {' '}
                  {item.type === 'highActivity'
                    ? `${item.change}% vol/tvl`
                    : `${item.change.toFixed(1)}%`
                  }
                </span>
              )}
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 8,
                color: tagStyle.color,
                background: tagStyle.bg,
                border: `1px solid ${tagStyle.border}`,
                padding: '1px 5px',
                borderRadius: 3,
                fontWeight: 600,
                letterSpacing: 0.3,
              }}>
                {item.label.toUpperCase()}
              </span>
            </div>
          );
        })}
      </div>

      {/* Right fade */}
      <div style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: 60,
        background: colors.trendingFadeRight,
        zIndex: 2,
      }} />

      <style>{`
        @keyframes trendingScroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @keyframes trendingPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
